import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Card from '../common/Card';

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

const PieChartCard = ({ title, data, total, fieldName, onSliceClick }) => {
  // Take top 6 values for pie chart (too many slices are hard to read)
  const chartData = data.slice(0, 6).map((item, index) => ({
    name: item.value,
    value: item.count,
    percentage: item.percentage,
    color: COLORS[index % COLORS.length]
  }));

  // Calculate "Others" if there are more than 6 values
  const hasOthers = data.length > 6;
  if (hasOthers) {
    const othersCount = data.slice(6).reduce((sum, item) => sum + item.count, 0);
    const othersPercentage = data.slice(6).reduce((sum, item) => sum + item.percentage, 0);
    chartData.push({
      name: 'Others',
      value: othersCount,
      percentage: othersPercentage,
      color: '#9CA3AF' // Gray
    });
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900">{payload[0].name}</p>
          <p className="text-sm text-gray-600">
            Count: <span className="font-bold">{payload[0].value}</span>
          </p>
          <p className="text-sm text-gray-600">
            Percentage: <span className="font-bold">{payload[0].payload.percentage.toFixed(1)}%</span>
          </p>
          {onSliceClick && payload[0].name !== 'Others' && (
            <p className="text-xs text-blue-600 mt-1 italic">Click to view attendees</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="chart-container shadow-sm border-gray-200" id={`chart-${fieldName}`}>
      <Card.Content className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        
        {chartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => 
                    percentage > 5 ? `${name.length > 12 ? name.substring(0, 10) + '...' : name}: ${percentage.toFixed(1)}%` : ''
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(data, index) => {
                    if (onSliceClick && data.name !== 'Others') {
                      onSliceClick(fieldName, data.name, title);
                    }
                  }}
                  className={onSliceClick ? "cursor-pointer" : ""}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color} 
                      style={{ outline: 'none' }}
                      className={onSliceClick && entry.name !== 'Others' ? "hover:opacity-80 transition-opacity" : ""}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                  formatter={(value, entry) => (
                    <span className="text-sm">
                      {value} ({entry.payload.value})
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>

            {/* Summary below chart */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Total responses:</span>
                <span className="font-bold text-gray-900">{total}</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-600">Unique values:</span>
                <span className="font-bold text-gray-900">{data.length}</span>
              </div>
            </div>
          </>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            No data available
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

export default PieChartCard;
